<template>
  <div class="flex w-full flex-col space-y-4">
    <!-- Header Section -->
    <div class="flex items-center justify-start">
      <h1 class="text-5xl font-medium text-gray-900">{{ __('Wishlist') }}</h1>
    </div>

    <!-- Search and Filter Section -->
    <div class="flex flex-col items-center pt-4">
      <div class="relative flex w-full items-center justify-start">
        <Input
          v-model="searchString"
          :value="searchString"
          :placeholder="__('Search Products...')"
          type="text"
          :maxlength="100"
          aria-label="Search Products"
          @input="handleSearchInput"
          icon="search"
          placeholder="Search item"
          class="w-full"
        />
      </div>
    </div>

    <!-- Table Container -->

    <div
      class="scrollbar-thin grid h-[600px] grid-cols-1 gap-x-10 gap-y-20 overflow-y-auto rounded-lg bg-white p-8 shadow-md md:grid-cols-2 lg:grid-cols-4"
    >
      <template v-if="!isLoadingProducts && products.data.length > 0">
        <div
          v-for="(product, index) in products.data"
          :key="product.id"
          class="hover-lift-no-shadow flex flex-col gap-y-5"
        >
          <!-- Image -->
          <div
            @click.prevent="
              redirectToPage(
                'redesign.candidate.shop.products.show',
                product.id
              )
            "
            class="relative aspect-square cursor-pointer overflow-hidden rounded-lg shadow-md transition-all duration-200 hover:shadow-lg"
          >
            <img
              :src="`/uploads/${product.image.path}`"
              :key="product.image.id"
              :alt="__('Media image')"
              class="h-full w-full object-cover"
            />
            <Icon
              v-if="product.unlocked"
              name="jobful_unlocked"
              class="absolute right-0 top-2"
            >
            </Icon>
            <Icon v-else name="jobful_locked" class="absolute right-0 top-2">
            </Icon>
            <div class="absolute bottom-2 left-4 flex flex-col gap-y-2">
              <div
                class="flex w-fit items-center justify-start rounded-xl bg-gray-50 px-2 py-0.5 text-sm text-zinc-800"
              >
                {{ product.category_name }}
              </div>
              <div
                class="flex w-fit items-center justify-start rounded-xl bg-gray-50 px-2 py-0.5 text-sm text-zinc-800"
              >
                <span
                  class="inline-block max-w-32 overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {{ product.company_name }}
                </span>
              </div>
            </div>
          </div>
          <!-- Product Info -->
          <div class="flex flex-col gap-y-4">
            <div class="flex items-center justify-between">
              <span class="max-w-[200px] font-medium">{{ product.name }} </span>

              <Icon
                @click.prevent="handleToggleWishList(product.id)"
                name="favorite"
                class="cursor-pointer text-primary transition-all duration-200"
                :type="product.is_in_wishlist ? 'filled' : 'outlined'"
              >
              </Icon>
            </div>

            <div class="flex items-center justify-start gap-x-2">
              <span>{{ product.points }} </span>
              <Icon name="jobful_points_variant"></Icon>
            </div>

            <div class="flex items-center justify-start gap-x-2">
              <template v-if="product.stock_status['in_stock']">
                <Icon
                  name="assignment_late"
                  type="filled"
                  class="text-[#10B981]"
                ></Icon>
                <span class="text-base font-medium">{{ __('In stock') }}</span>
              </template>

              <template v-if="product.stock_status['low_stock']">
                <Icon
                  name="assignment_late"
                  type="filled"
                  class="text-[#FFCC24]"
                ></Icon>
                <span class="text-base font-medium">{{ __('Low stock') }}</span>
              </template>

              <template v-if="product.stock_status['out_of_stock']">
                <Icon
                  name="assignment_late"
                  type="filled"
                  class="text-[#EF4444]"
                ></Icon>
                <span class="text-base font-medium">{{
                  __('Out of stock')
                }}</span>
              </template>
            </div>
          </div>
        </div>
      </template>

      <div
        class="col-span-1 flex items-center justify-center md:col-span-2 lg:col-span-4"
        v-else-if="!isLoadingProducts && products.data.length === 0"
      >
        <span
          class="flex h-full w-full items-center justify-center px-6 py-8 text-gray-500"
        >
          {{ __('No results found') }}
        </span>
      </div>

      <div
        class="col-span-1 flex items-center justify-center md:col-span-2 lg:col-span-4"
        v-else-if="isLoadingProducts"
      >
        <div class="flex h-full w-full items-center justify-center gap-x-2">
          <span class="font-medium text-primary">
            {{ __('Loading') }}
          </span>
          <Icon
            class="animate-spin text-2xl text-primary"
            name="progress_activity"
          />
        </div>
      </div>
    </div>
  </div>

  <div class="mt-auto">
    <Pagination
      v-if="products.meta?.last_page > 1"
      :key="paginationKey"
      :current-page="products.meta.current_page"
      :last-page="products.meta.last_page"
      class="mt-6"
      @update:current-page="handlePageChange({ page: $event, reset: false })"
    />
  </div>
</template>
<script setup>
  import Input from '@/components/global/Form/Input.vue';
  import Icon from '@/components/global/Icon.vue';
  import Pagination from '@/components/global/Pagination.vue';
  import { nextTick, onMounted, ref } from 'vue';
  import { useToast } from 'vue-toastification';

  const props = defineProps({
    data: {
      type: Object,
      default: () => ({})
    },
    errors: {
      type: Object,
      default: () => ({})
    }
  });

  const products = ref(props.data.products);
  const paginationKey = ref(0);

  const redirectToPage = (name, params) => {
    window.location.href = route(name, params);
  };

  const searchString = ref('');
  const toast = useToast();

  let searchTimeout = null;

  const isLoadingProducts = ref(false);

  const handleSearchInput = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      handlePageChange({
        page: 1,
        reset: true
      });
    }, 300);
  };

  const handlePageChange = async (eventData) => {
    isLoadingProducts.value = true;

    if (eventData.reset) {
      isLoadingProducts.value = true;
    }

    try {
      const response = await axios.get(
        route('redesign.candidate.shop.products.load'),
        {
          params: {
            page: eventData.page || 1,
            reset: eventData.reset || false,
            search_string: searchString.value,
            wishlist: true
          }
        }
      );

      products.value = response.data.data.products;
      products.value.meta.last_page =
        response.data.data.products.meta.last_page;
      products.value.meta.current_page =
        response.data.data.products.meta.current_page;
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      isLoadingProducts.value = false;
    }
  };

  const handleToggleWishList = async (productId) => {
    const response = await axios.post(
      route('redesign.candidate.shop.products.toggle_wishlist', productId)
    );

    if (response.data.message) {
      toast.success(response.data.message);
      products.value.data = products.value.data.map((product) => {
        if (product.id === productId) {
          return { ...product, is_in_wishlist: !product.is_in_wishlist };
        }
        return product;
      });
    } else {
      toast.error(response.data.message);
    }
  };
</script>
